import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Building2,
  Mail,
  Phone,
  Globe,
  MessageCircle,
  Users,
  User,
  Upload,
  Trash2,
  ImageIcon,
  Loader2,
  Save,
  Crown,
  DollarSign,
  Clock,
} from 'lucide-react';

interface AgencyData {
  id: string;
  name: string;
  code: string;
  contact_email: string | null;
  address: string | null;
  phone: string | null;
  manager_name: string | null;
  num_employees: number | null;
  website: string | null;
  whatsapp: string | null;
  logo_url: string | null;
  plan: string;
  state_billing_rate: number | null;
  caregiver_rate: number | null;
  patient_care_hours: number | null;
}

const AdminAgencyProfilePage = () => {
  const { user } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [agency, setAgency] = useState<AgencyData | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    contact_email: '',
    address: '',
    phone: '',
    manager_name: '',
    num_employees: '',
    website: '',
    whatsapp: '',
    state_billing_rate: '',
    caregiver_rate: '',
    patient_care_hours: '',
  });

  useEffect(() => {
    loadAgency();
  }, [user?.agency_id]);

  const loadAgency = async () => {
    if (!user?.agency_id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('id, name, code, contact_email, address, phone, manager_name, num_employees, website, whatsapp, logo_url, plan, state_billing_rate, caregiver_rate, patient_care_hours')
        .eq('id', user.agency_id)
        .single();

      if (error) throw error;

      if (data) {
        setAgency(data);
        setLogoUrl(data.logo_url);
        setFormData({
          name: data.name || '',
          contact_email: data.contact_email || '',
          address: data.address || '',
          phone: data.phone || '',
          manager_name: data.manager_name || '',
          num_employees: data.num_employees?.toString() || '',
          website: data.website || '',
          whatsapp: data.whatsapp || '',
          state_billing_rate: data.state_billing_rate?.toString() || '',
          caregiver_rate: data.caregiver_rate?.toString() || '',
          patient_care_hours: data.patient_care_hours?.toString() || '',
        });
      }
    } catch (error) {
      console.error('Error loading agency:', error);
      toast.error('Failed to load agency profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !agency) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file (JPG, PNG, etc.)');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Please select an image smaller than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${agency.id}-${Date.now()}.${fileExt}`;

      if (logoUrl) {
        const oldPath = logoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage.from('agency-logos').remove([oldPath]);
        }
      }

      const { error: uploadError } = await supabase.storage
        .from('agency-logos')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('agency-logos')
        .getPublicUrl(fileName);

      const newLogoUrl = urlData.publicUrl;

      const { error: updateError } = await supabase
        .from('agencies')
        .update({ logo_url: newLogoUrl })
        .eq('id', agency.id);

      if (updateError) throw updateError;

      setLogoUrl(newLogoUrl);
      toast.success('Logo uploaded successfully');
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast.error(error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    if (!agency || !logoUrl) return;

    setDeletingLogo(true);
    try {
      const fileName = logoUrl.split('/').pop();
      if (fileName) {
        await supabase.storage.from('agency-logos').remove([fileName]);
      }

      const { error } = await supabase
        .from('agencies')
        .update({ logo_url: null })
        .eq('id', agency.id);

      if (error) throw error;

      setLogoUrl(null);
      toast.success('Logo removed');
    } catch (error: any) {
      console.error('Error deleting logo:', error);
      toast.error(error.message || 'Failed to remove logo');
    } finally {
      setDeletingLogo(false);
    }
  };

  const handleSave = async () => {
    if (!agency) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('agencies')
        .update({
          name: formData.name.trim(),
          contact_email: formData.contact_email.trim() || null,
          address: formData.address.trim() || null,
          phone: formData.phone.trim() || null,
          manager_name: formData.manager_name.trim() || null,
          num_employees: formData.num_employees ? parseInt(formData.num_employees) : null,
          website: formData.website.trim() || null,
          whatsapp: formData.whatsapp.trim() || null,
          state_billing_rate: formData.state_billing_rate ? parseFloat(formData.state_billing_rate) : null,
          caregiver_rate: formData.caregiver_rate ? parseFloat(formData.caregiver_rate) : null,
          patient_care_hours: formData.patient_care_hours ? parseFloat(formData.patient_care_hours) : null,
        })
        .eq('id', agency.id);

      if (error) throw error;

      toast.success('Agency profile updated successfully');
      loadAgency();
    } catch (error: any) {
      console.error('Error saving agency:', error);
      toast.error(error.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AdminLayout>
    );
  }

  if (!agency) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">No Agency Found</h2>
          <p className="text-muted-foreground">You are not associated with any agency.</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Agency Profile | CheckFirst Admin</title>
        <meta name="description" content="Manage your agency profile and settings" />
      </Helmet>
      <AdminLayout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Agency Profile</h1>
                <p className="text-muted-foreground">Manage your agency information</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="gap-1">
                <Crown className="h-3 w-3" />
                <span className="capitalize">{agency.plan.replace('_', ' ')}</span>
              </Badge>
              <Badge variant="secondary">Code: {agency.code}</Badge>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {/* Logo Card */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Agency Logo</CardTitle>
                <CardDescription>Upload your agency's logo</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col items-center gap-4">
                  <Avatar className="h-32 w-32 border-2 border-border">
                    <AvatarImage src={logoUrl || undefined} alt={agency.name} />
                    <AvatarFallback className="bg-muted text-3xl">
                      <ImageIcon className="h-12 w-12 text-muted-foreground" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="logo-upload" className="cursor-pointer">
                      <div className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground transition-colors">
                        {uploadingLogo ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Upload
                          </>
                        )}
                      </div>
                      <Input
                        id="logo-upload"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                        className="hidden"
                      />
                    </Label>
                    {logoUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDeleteLogo}
                        disabled={deletingLogo}
                        className="text-destructive hover:text-destructive"
                      >
                        {deletingLogo ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Square image recommended. Max 5MB.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Main Form */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Agency Information</CardTitle>
                <CardDescription>Update your agency's basic information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Basic Info */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Agency Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Agency name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address">Address</Label>
                    <Textarea
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Full address"
                      rows={2}
                    />
                  </div>
                </div>

                <Separator />

                {/* Contact Info */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Contact Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email" className="flex items-center gap-1.5">
                        <Mail className="h-3.5 w-3.5" />
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => setFormData(prev => ({ ...prev, contact_email: e.target.value }))}
                        placeholder="contact@agency.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="flex items-center gap-1.5">
                        <Phone className="h-3.5 w-3.5" />
                        Phone
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="website" className="flex items-center gap-1.5">
                        <Globe className="h-3.5 w-3.5" />
                        Website
                      </Label>
                      <Input
                        id="website"
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                        placeholder="https://www.agency.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="whatsapp" className="flex items-center gap-1.5">
                        <MessageCircle className="h-3.5 w-3.5" />
                        WhatsApp
                      </Label>
                      <Input
                        id="whatsapp"
                        type="tel"
                        value={formData.whatsapp}
                        onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Staff Info */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Staff Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="manager" className="flex items-center gap-1.5">
                        <User className="h-3.5 w-3.5" />
                        Manager Name
                      </Label>
                      <Input
                        id="manager"
                        value={formData.manager_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, manager_name: e.target.value }))}
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="employees" className="flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        Number of Employees
                      </Label>
                      <Input
                        id="employees"
                        type="number"
                        min="0"
                        value={formData.num_employees}
                        onChange={(e) => setFormData(prev => ({ ...prev, num_employees: e.target.value }))}
                        placeholder="10"
                      />
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Billing Configuration */}
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                    Billing & Reimbursement Settings
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Configure state billing rates, caregiver rates, and approved care hours for cost savings calculations.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="state_billing_rate" className="flex items-center gap-1.5">
                        <DollarSign className="h-3.5 w-3.5" />
                        State Billing Rate ($/hr)
                      </Label>
                      <Input
                        id="state_billing_rate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.state_billing_rate}
                        onChange={(e) => setFormData(prev => ({ ...prev, state_billing_rate: e.target.value }))}
                        placeholder="25.00"
                      />
                      <p className="text-xs text-muted-foreground">
                        Hourly rate reimbursed by Medicaid/Medicare
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="caregiver_rate" className="flex items-center gap-1.5">
                        <DollarSign className="h-3.5 w-3.5" />
                        Caregiver Rate ($/hr)
                      </Label>
                      <Input
                        id="caregiver_rate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.caregiver_rate}
                        onChange={(e) => setFormData(prev => ({ ...prev, caregiver_rate: e.target.value }))}
                        placeholder="15.00"
                      />
                      <p className="text-xs text-muted-foreground">
                        Hourly rate paid to caregivers
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="patient_care_hours" className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />
                        Patient Care Hours (per week)
                      </Label>
                      <Input
                        id="patient_care_hours"
                        type="number"
                        min="0"
                        step="0.5"
                        value={formData.patient_care_hours}
                        onChange={(e) => setFormData(prev => ({ ...prev, patient_care_hours: e.target.value }))}
                        placeholder="20"
                      />
                      <p className="text-xs text-muted-foreground">
                        Default approved hours by Dept. of Aging
                      </p>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Save Button */}
                <div className="flex justify-end">
                  <Button onClick={handleSave} disabled={saving || !formData.name.trim()}>
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminAgencyProfilePage;
